import { useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarIcon, Utensils, Car, Cctv, Droplets, GlassWater, BedDouble, LogIn, BedSingle, Coffee, Sandwich, IdCard } from "lucide-react";
import PageBanner from "../components/PageBanner";
import Seo from "../components/Seo";
import { Reveal, SectionLabel } from "../components/Motion";
import { useApp, api } from "../AppContext";
import { C, UI, IMAGES } from "../content";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";

const AMEN_ICONS = { utensils: Utensils, sandwich: Sandwich, coffee: Coffee, car: Car, cctv: Cctv, droplets: Droplets, "glass-water": GlassWater, "bed-double": BedDouble };

function DateField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-earth-800 font-semibold mb-2 block">{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" data-testid={testid} className="w-full justify-start text-left font-normal border-earth-900/20 h-12 text-base">
            <CalendarIcon className="mr-2 h-4 w-4 text-saffron-600" />
            {value ? format(value, "PPP") : <span className="text-earth-500">Select date</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={value} onSelect={onChange} initialFocus disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function BhaktNiwas() {
  const { t, lang } = useApp();
  const [form, setForm] = useState({ name: "", phone: "", email: "", room_type: "", guests: "1", checkin_time: "", checkout_time: "", vehicle_number: "", id_type: "aadhaar", id_number: "", message: "" });
  const [checkin, setCheckin] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const rooms = C.bhaktNiwas.rooms.map((r) => (lang === "mr" ? r.mr : r.en));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.room_type || !checkin || !checkout) {
      toast.error(lang === "mr" ? "कृपया सर्व आवश्यक माहिती भरा." : "Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/booking", {
        ...form, guests: parseInt(form.guests) || 1,
        checkin: format(checkin, "yyyy-MM-dd"), checkout: format(checkout, "yyyy-MM-dd"),
      });
      toast.success(lang === "mr" ? "आपली आरक्षण विनंती पाठवली गेली आहे. ट्रस्ट लवकरच संपर्क करेल." : "Your booking enquiry has been sent. The Trust will contact you soon.");
      setForm({ name: "", phone: "", email: "", room_type: "", guests: "1", checkin_time: "", checkout_time: "", vehicle_number: "", id_type: "aadhaar", id_number: "", message: "" });
      setCheckin(null); setCheckout(null);
    } catch {
      toast.error(lang === "mr" ? "काहीतरी चूक झाली. पुन्हा प्रयत्न करा." : "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <Seo title="Bhakt Niwas — Pilgrim Stay & Booking" description="Stay at the Narayananand Tirth Swami Bhakt Niwas, ~300m from the Datta Mandir, Audumbar. AC & non-AC rooms and suites, meals, parking, CCTV, hot water. Send a booking enquiry." />
      <PageBanner label={lang === "mr" ? "निवास" : "Stay"} titleObj={C.bhaktNiwas} subObj={C.bhaktNiwas} image={IMAGES.bhaktNiwasBuilding} />

      <section className="py-16 lg:py-24 max-w-[1400px] mx-auto px-4 lg:px-8">
        {/* Room types */}
        <Reveal><SectionLabel>{lang === "mr" ? "खोल्यांचे प्रकार" : "Room Types"}</SectionLabel></Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {C.bhaktNiwas.rooms.map((r, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="rounded-2xl border border-earth-900/10 bg-white p-6 h-full" data-testid={`room-type-${i}`}>
                <BedSingle className="text-saffron-600 mb-3" size={26} />
                <div className="font-serif-head text-xl text-earth-900">{r[lang] ?? r.en}</div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Amenities */}
        <Reveal><SectionLabel>{lang === "mr" ? "सुविधा" : "Amenities"}</SectionLabel></Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {C.bhaktNiwas.amenities.map((a, i) => {
            const Icon = AMEN_ICONS[a.icon];
            return (
              <Reveal key={i} delay={i * 0.05}>
                <div className="rounded-2xl border border-earth-900/10 bg-white p-5 flex flex-col items-center text-center gap-3">
                  <div className="text-saffron-600"><Icon size={26} /></div>
                  <span className="text-earth-800 font-medium">{a[lang] ?? a.en}</span>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal>
          <div className="flex flex-wrap gap-4 mb-14">
            <div className="flex items-start gap-3 rounded-xl bg-saffron-500/10 border border-saffron-500/30 px-5 py-3 sm:col-span-2" data-testid="niwas-checkin-note"><LogIn size={18} className="text-deepred-700 mt-0.5 shrink-0" /><span className="text-earth-800"><b>{lang === "mr" ? "आगमन" : "Arrival"}:</b> {lang === "mr" ? C.bhaktNiwas.checkin_note_mr : C.bhaktNiwas.checkin_note_en}</span></div>
          </div>
        </Reveal>

        {/* Photos */}
        <Reveal><SectionLabel>{lang === "mr" ? "खोल्यांची झलक" : "Room Photos"}</SectionLabel></Reveal>
        <div className="grid sm:grid-cols-3 gap-4 mb-16" data-testid="niwas-photos">
          <Reveal className="sm:col-span-3">
            <div className="rounded-2xl overflow-hidden aspect-[21/9] border border-earth-900/10 shadow-[0_16px_40px_rgba(59,34,19,0.12)]">
              <img src={IMAGES.bhaktNiwasBuilding} alt="Bhakt Niwas building — Audumbar" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          {[IMAGES.niwasCorridor, IMAGES.niwasStreet, IMAGES.courtyardShrine].map((u, i) => (
            <Reveal key={i} delay={i * 0.06}><div className="rounded-2xl overflow-hidden aspect-[4/3] border border-earth-900/10"><img src={u} alt="Bhakt Niwas room" loading="lazy" className="w-full h-full object-cover" /></div></Reveal>
          ))}
        </div>

        {/* Booking form */}
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <Reveal>
            <SectionLabel>{lang === "mr" ? "आरक्षण विनंती" : "Booking Enquiry"}</SectionLabel>
            <h2 className="font-serif-head text-3xl lg:text-5xl text-earth-900 font-bold">{lang === "mr" ? "आपला मुक्काम निश्चित करा" : "Plan Your Stay"}</h2>
            <p className="mt-4 text-earth-800/80 text-lg">{lang === "mr" ? "खालील फॉर्म भरा; ट्रस्ट आपल्याशी लवकरच संपर्क साधेल." : "Fill the form below and the Trust will get in touch with you shortly."}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="rounded-3xl border border-earth-900/10 bg-white p-6 lg:p-8 space-y-4" data-testid="booking-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-earth-800 font-semibold mb-2 block">{UI.name[lang]} *</Label><Input data-testid="booking-name" value={form.name} onChange={set("name")} className="h-12 border-earth-900/20" /></div>
                <div><Label className="text-earth-800 font-semibold mb-2 block">{UI.phone[lang]} *</Label><Input data-testid="booking-phone" value={form.phone} onChange={set("phone")} className="h-12 border-earth-900/20" /></div>
              </div>
              <div><Label className="text-earth-800 font-semibold mb-2 block">{UI.email[lang]} *</Label><Input type="email" data-testid="booking-email" value={form.email} onChange={set("email")} className="h-12 border-earth-900/20" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <DateField label={`${UI.checkin[lang]} *`} value={checkin} onChange={setCheckin} testid="booking-checkin" />
                <DateField label={`${UI.checkout[lang]} *`} value={checkout} onChange={setCheckout} testid="booking-checkout" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-earth-800 font-semibold mb-2 block">{UI.checkin_time[lang]}</Label><Input data-testid="booking-checkin-time" placeholder="e.g. 5:00 PM" value={form.checkin_time} onChange={set("checkin_time")} className="h-12 border-earth-900/20" /></div>
                <div><Label className="text-earth-800 font-semibold mb-2 block">{UI.checkout_time[lang]}</Label><Input data-testid="booking-checkout-time" placeholder="e.g. 10:00 AM" value={form.checkout_time} onChange={set("checkout_time")} className="h-12 border-earth-900/20" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-earth-800 font-semibold mb-2 block">{UI.room_type[lang]} *</Label>
                  <Select value={form.room_type} onValueChange={set("room_type")}>
                    <SelectTrigger data-testid="booking-room-type" className="h-12 border-earth-900/20"><SelectValue placeholder={lang === "mr" ? "निवडा" : "Select"} /></SelectTrigger>
                    <SelectContent>
                      {rooms.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-earth-800 font-semibold mb-2 block">{UI.guests[lang]}</Label><Input type="number" min="1" data-testid="booking-guests" value={form.guests} onChange={set("guests")} className="h-12 border-earth-900/20" /></div>
              </div>
              <div><Label className="text-earth-800 font-semibold mb-2 block">{UI.vehicle[lang]}</Label><Input data-testid="booking-vehicle" placeholder="e.g. MH 09 AB 1234" value={form.vehicle_number} onChange={set("vehicle_number")} className="h-12 border-earth-900/20" /></div>
              <div>
                <Label className="text-earth-800 font-semibold mb-2 block flex items-center gap-2"><IdCard size={16} className="text-saffron-600" />{lang === "mr" ? "आधार कार्ड क्रमांक" : "Aadhaar Card Number"} *</Label>
                <Input data-testid="booking-id-number" placeholder="1234 5678 9012" inputMode="numeric" maxLength={14} value={form.id_number} onChange={set("id_number")} className="h-12 border-earth-900/20" />
                <p className="mt-1.5 text-xs text-earth-600">{lang === "mr" ? "पत्ता पडताळणी व सुरक्षिततेसाठी आवश्यक." : "Required to verify address and complete due diligence."}</p>
              </div>
              <div><Label className="text-earth-800 font-semibold mb-2 block">{UI.message[lang]}</Label><Textarea data-testid="booking-message" value={form.message} onChange={set("message")} className="border-earth-900/20 min-h-24" /></div>
              <Button type="submit" disabled={loading} data-testid="booking-submit" className="w-full h-12 bg-saffron-500 hover:bg-saffron-600 text-white text-base font-semibold">{loading ? "..." : UI.book_now[lang]}</Button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
